import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  ShieldCheckIcon,
  KeyIcon,
  BellIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import PermissionsModal from '../components/PermissionsModal';
import NotificationsModal from '../components/NotificationsModal';

export default function SuperAdminManageAdmins() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAdminForPermissions, setSelectedAdminForPermissions] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [selectedAdminForNotifications, setSelectedAdminForNotifications] = useState(null);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    password: '',
    organismes: [],
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchAdmins();
  }, [user, navigate]);

  useEffect(() => {
    if (editingAdmin || !showModal || createNewAccount) {
      setMemberResults([]);
      return;
    }
    const q = memberSearch.trim();
    if (q.length < 2) {
      setMemberResults([]);
      return;
    }
    const token = localStorage.getItem('token');
    const handle = setTimeout(async () => {
      setSearchingMembers(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/members-search`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { q },
          }
        );
        setMemberResults(response.data || []);
      } catch (error) {
        console.error('Erreur recherche membres:', error);
        setMemberResults([]);
      } finally {
        setSearchingMembers(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [memberSearch, showModal, editingAdmin, createNewAccount]);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Admins récupérés du backend:', response.data);
      setAdmins(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des admins:', error);
      alert('Erreur lors du chargement des administrateurs');
    } finally {
      setLoading(false);
    }
  };

  const getAdminOrganismes = (admin) => {
    if (admin.organismes && Array.isArray(admin.organismes) && admin.organismes.length > 0) {
      return admin.organismes;
    }
    if (admin.organisme) return [admin.organisme];
    return [];
  };

  const handleExportPermissionsExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const structureRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/permissions/structure`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const structure = structureRes.data || {};

      const sortedAdmins = [...admins].sort((a, b) => {
        const orgA = getAdminOrganismes(a).join(', ');
        const orgB = getAdminOrganismes(b).join(', ');
        if (orgA !== orgB) return orgA.localeCompare(orgB, 'fr');
        return `${a.nom || ''} ${a.prenom || ''}`.localeCompare(`${b.nom || ''} ${b.prenom || ''}`, 'fr');
      });

      const colCount = 1 + sortedAdmins.length;
      const emptyCells = () => Array(colCount).fill('');

      const headerOrganismes = ['', ...sortedAdmins.map((admin) => getAdminOrganismes(admin).join(', '))];
      const headerNoms = [
        '',
        ...sortedAdmins.map((admin) => `${admin.prenom || ''} ${String(admin.nom || '').toUpperCase()}`.trim()),
      ];
      const headerTitres = ['', ...sortedAdmins.map((admin) => admin.titreBureau || '')];

      const rows = [headerOrganismes, headerNoms, headerTitres];
      const sectionRowIndexes = [];

      Object.keys(structure).forEach((moduleKey) => {
        const module = structure[moduleKey];
        const sectionRow = emptyCells();
        sectionRow[0] = module.label || moduleKey;
        sectionRowIndexes.push(rows.length);
        rows.push(sectionRow);
        (module.permissions || []).forEach((perm) => {
          const cells = [perm.label || perm.key];
          sortedAdmins.forEach((admin) => {
            const active = Boolean(admin.permissions?.[moduleKey]?.[perm.key]);
            cells.push(active ? 'oui' : '');
          });
          rows.push(cells);
        });
        rows.push(emptyCells());
      });

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 52 },
        ...sortedAdmins.map(() => ({ wch: 22 })),
      ];
      worksheet['!views'] = [{ state: 'frozen', ySplit: 3, xSplit: 1 }];

      const nameStyle = { font: { bold: true } };
      const sectionStyle = {
        font: { bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: 'E5E7EB' } },
      };

      const ensureCell = (r, c) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[addr]) worksheet[addr] = { t: 's', v: '' };
        return worksheet[addr];
      };

      for (let c = 1; c < colCount; c += 1) {
        ensureCell(1, c).s = nameStyle;
      }

      sectionRowIndexes.forEach((r) => {
        for (let c = 0; c < colCount; c += 1) {
          ensureCell(r, c).s = sectionStyle;
        }
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Permissions');

      const now = new Date();
      const jj = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const aa = String(now.getFullYear()).slice(-2);
      XLSX.writeFile(workbook, `permissions_${jj}${mm}${aa}.xlsx`);
    } catch (error) {
      console.error('Erreur export permissions:', error);
      alert("Erreur lors de l'export Excel des permissions");
    } finally {
      setExporting(false);
    }
  };

  const handleOpenModal = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      
      // Déterminer les organismes (nouveau format ou ancien)
      let organismes = [];
      if (admin.organismes && Array.isArray(admin.organismes) && admin.organismes.length > 0) {
        organismes = admin.organismes;
      } else if (admin.organisme) {
        organismes = [admin.organisme];
      }
      
      console.log('Admin à éditer:', admin);
      console.log('Organismes détectés:', organismes);
      
      setFormData({
        prenom: admin.prenom,
        nom: admin.nom,
        email: admin.email,
        password: '',
        organismes: organismes,
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        prenom: '',
        nom: '',
        email: '',
        password: '',
        organismes: [],
      });
      setMemberSearch('');
      setMemberResults([]);
      setSelectedMember(null);
      setCreateNewAccount(false);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setMemberSearch('');
    setMemberResults([]);
    setSelectedMember(null);
    setCreateNewAccount(false);
  };

  const handleSelectMember = (member) => {
    if (member.dejaAdmin) return;
    setSelectedMember(member);
    setMemberSearch('');
    setMemberResults([]);
    setCreateNewAccount(false);
    setFormData((prev) => ({
      ...prev,
      prenom: member.prenom || '',
      nom: member.nom || '',
      email: member.email || '',
      password: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation : au moins un organisme requis
    if (!formData.organismes || formData.organismes.length === 0) {
      alert('Veuillez sélectionner au moins un organisme');
      return;
    }

    if (!editingAdmin && !selectedMember && !createNewAccount) {
      alert('Recherchez et sélectionnez un membre, ou créez un nouveau compte.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const dataToSend = { ...formData };
      
      // Ne pas envoyer le mot de passe s'il est vide lors de l'édition
      if (editingAdmin && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (editingAdmin) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/users/admin/${editingAdmin._id}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Administrateur modifié avec succès');
      } else if (selectedMember) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/users/admins`,
          { userId: selectedMember._id, organismes: formData.organismes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(
          response.data?.promoted
            ? 'Droits administrateur ajoutés au compte membre existant. Le mot de passe reste celui de l’espace adhérent.'
            : 'Administrateur créé avec succès'
        );
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/users/admins`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(
          response.data?.promoted
            ? 'Un compte membre existait déjà avec cet email : les droits administrateur lui ont été ajoutés.'
            : 'Administrateur créé avec succès'
        );
      }
      
      handleCloseModal();
      fetchAdmins();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (admin) => {
    const nom = `${admin.prenom || ''} ${admin.nom || ''}`.trim() || 'cet administrateur';
    if (
      !window.confirm(
        `Retirer les droits administrateur de ${nom} ?\n\nLe compte membre et les adhésions sont conservés.`
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/users/admin/${admin._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Droits administrateur retirés. Le compte membre a été conservé.');
      fetchAdmins();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleOpenPermissionsModal = (admin) => {
    setSelectedAdminForPermissions(admin);
    setShowPermissionsModal(true);
  };

  const handleClosePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedAdminForPermissions(null);
  };

  const handleOpenNotificationsModal = (admin) => {
    setSelectedAdminForNotifications(admin);
    setShowNotificationsModal(true);
  };

  const handleCloseNotificationsModal = () => {
    setShowNotificationsModal(false);
    setSelectedAdminForNotifications(null);
  };

  const handleResetPassword = async (adminId) => {
    if (!window.confirm('Réinitialiser le mot de passe de cet administrateur ? Un nouveau mot de passe temporaire sera envoyé par email.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin-management/admins/${adminId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Mot de passe réinitialisé avec succès.\nMot de passe temporaire : ${response.data.tempPassword}\n\nCe mot de passe a été envoyé par email à l'administrateur.`);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la réinitialisation du mot de passe');
    }
  };


  const handleOrganismeToggle = (organisme) => {
    const newOrganismes = formData.organismes.includes(organisme)
      ? formData.organismes.filter(o => o !== organisme)
      : [...formData.organismes, organisme];
    
    setFormData({
      ...formData,
      organismes: newOrganismes,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">Abeille Réunion</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="font-medium text-gray-900">Super Admin</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Administrateurs</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Administrateurs</h1>
            <p className="mt-2 text-sm text-gray-600">
              Gérez les comptes administrateurs et leurs permissions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleExportPermissionsExcel}
              disabled={exporting || loading || admins.length === 0}
              className="inline-flex items-center px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {exporting ? 'Export…' : 'Export Excel'}
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nouvel administrateur
            </button>
          </div>
        </div>
      </div>

      {/* Liste des admins */}
      <div className="px-8 pb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Administrateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisme
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notifications
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {admin.prenom?.[0]}{admin.nom?.[0]}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {admin.prenom} {admin.nom}
                        </div>
                        <div className="text-sm text-gray-500">{admin.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(admin.organismes && admin.organismes.length > 0 
                        ? admin.organismes 
                        : (admin.organisme ? [admin.organisme] : [])
                      ).map((org) => (
                        <span key={org} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          org === 'SAR' 
                            ? 'bg-indigo-100 text-indigo-800' 
                            : 'bg-pink-100 text-pink-800'
                        }`}>
                          {org}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {admin.permissions?.communications?.access && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Communications
                        </span>
                      )}
                      {admin.permissions?.actualites?.access && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Actualités
                        </span>
                      )}
                      {admin.permissions?.adhesions?.access && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Adhésions
                        </span>
                      )}
                      {admin.permissions?.adhesions?.sendPaymentLink && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-honey-100 text-honey-800">
                          Lien paiement
                        </span>
                      )}
                      {admin.permissions?.adhesions?.changeAdherentStatus && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Changer statut
                        </span>
                      )}
                      {admin.permissions?.miellerie?.access && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Miellerie
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {admin.notificationSettings?.adhesions?.suiviAdhesions && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-honey-100 text-honey-800">
                          Suivi adhésions
                        </span>
                      )}
                      {!admin.notificationSettings?.adhesions?.suiviAdhesions && (
                        <span className="text-xs text-gray-400">Aucune</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => handleOpenPermissionsModal(admin)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Gérer les permissions"
                      >
                        <ShieldCheckIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleOpenNotificationsModal(admin)}
                        className="text-honey-600 hover:text-honey-900"
                        title="Gérer les notifications"
                      >
                        <BellIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(admin._id)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Réinitialiser le mot de passe"
                      >
                        <KeyIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleOpenModal(admin)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Modifier"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(admin)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingAdmin ? 'Modifier l\'administrateur' : 'Nouvel administrateur'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un membre *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Si la personne a déjà un compte adhérent, sélectionnez-le : les droits admin
                    seront ajoutés à ce même compte (adhésions et mot de passe conservés).
                  </p>
                  {!selectedMember && (
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => {
                          setMemberSearch(e.target.value);
                          setCreateNewAccount(false);
                        }}
                        placeholder="Nom, prénom ou email…"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {searchingMembers && (
                    <p className="text-sm text-gray-500 mt-2">Recherche…</p>
                  )}

                  {!selectedMember && memberSearch.trim().length >= 2 && !searchingMembers && memberResults.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-md max-h-56 overflow-y-auto">
                      {memberResults.map((m) => (
                        <button
                          key={m._id}
                          type="button"
                          disabled={m.dejaAdmin}
                          onClick={() => handleSelectMember(m)}
                          className={`w-full px-4 py-2 text-left border-b border-gray-100 last:border-b-0 ${
                            m.dejaAdmin
                              ? 'bg-gray-50 cursor-not-allowed opacity-70'
                              : 'hover:bg-indigo-50'
                          }`}
                        >
                          <div className="font-medium text-gray-900">
                            {m.prenom} {m.nom}
                            {m.raisonSociale ? ` (${m.raisonSociale})` : ''}
                          </div>
                          <div className="text-sm text-gray-500">{m.email}</div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {m.dejaAdmin
                              ? 'Déjà administrateur'
                              : m.organismesAdhesion?.length
                                ? `Adhérent : ${m.organismesAdhesion.join(', ')}`
                                : 'Compte membre, aucune adhésion active'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {!selectedMember && memberSearch.trim().length >= 2 && !searchingMembers && memberResults.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Aucun membre ne correspond. Vous pouvez créer un nouveau compte ci-dessous.
                    </p>
                  )}

                  {selectedMember && (
                    <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md flex items-start justify-between">
                      <div>
                        <div className="font-medium text-indigo-950">
                          {selectedMember.prenom} {selectedMember.nom}
                        </div>
                        <div className="text-sm text-indigo-800">{selectedMember.email}</div>
                        <div className="text-xs text-indigo-700 mt-1">
                          Compte existant : les droits admin seront ajoutés à ce membre.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMember(null);
                          setFormData((prev) => ({
                            ...prev,
                            prenom: '',
                            nom: '',
                            email: '',
                            password: '',
                          }));
                        }}
                        className="text-indigo-700 hover:text-indigo-900"
                        aria-label="Retirer la sélection"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}

                  {!selectedMember && (
                    <button
                      type="button"
                      onClick={() => {
                        setCreateNewAccount(true);
                        setSelectedMember(null);
                        setMemberResults([]);
                      }}
                      className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Cette personne n’a pas de compte → créer un nouveau compte
                    </button>
                  )}
                </div>
              )}

              {(editingAdmin || createNewAccount) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              )}

              {(editingAdmin || createNewAccount) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              )}

              {(editingAdmin || createNewAccount) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe {editingAdmin ? '(laisser vide pour ne pas modifier)' : '*'}
                </label>
                <input
                  type="password"
                  required={!editingAdmin}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Organismes * (au moins un requis)
                </label>
                <div className="space-y-2 p-3 border border-gray-300 rounded-md bg-gray-50">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.organismes.includes('SAR')}
                      onChange={() => handleOrganismeToggle('SAR')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      <span className="font-medium">SAR</span> - Syndicat Apicole de la Réunion
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.organismes.includes('AMAIR')}
                      onChange={() => handleOrganismeToggle('AMAIR')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      <span className="font-medium">AMAIR</span> - Association de la Maison de l'Apiculture de l'Île de la Réunion
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.organismes.includes('ADAR')}
                      onChange={() => handleOrganismeToggle('ADAR')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      <span className="font-medium">ADAR</span> - Association pour le Développement de l'Apiculture à la Réunion
                    </span>
                  </label>
                </div>
                {formData.organismes.length === 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    Veuillez sélectionner au moins un organisme
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note :</strong> Les permissions sont gérées après la création via le bouton « Gérer les permissions ».
                  {selectedMember
                    ? ' Le membre se connecte avec son mot de passe actuel.'
                    : ''}
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!editingAdmin && !selectedMember && !createNewAccount}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingAdmin ? 'Modifier' : selectedMember ? 'Nommer administrateur' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de gestion des permissions */}
      {showPermissionsModal && selectedAdminForPermissions && (
        <PermissionsModal
          admin={selectedAdminForPermissions}
          onClose={handleClosePermissionsModal}
          onSave={fetchAdmins}
        />
      )}

      {/* Modal de gestion des notifications */}
      {showNotificationsModal && selectedAdminForNotifications && (
        <NotificationsModal
          admin={selectedAdminForNotifications}
          onClose={handleCloseNotificationsModal}
          onSave={fetchAdmins}
        />
      )}
    </div>
  );
}
